import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import {
  Cancel,
  Close,
  Inventory2Outlined,
  KeyboardArrowDown,
  LocalShippingOutlined,
  SaveOutlined,
  South,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useModalStyles } from "../../common/useModalStyles";
import {
  extractShippingCharge,
  formatShippingCurrency,
  formatShippingMethodLabel,
  FULFILLMENT_SHIPPING_CHARGE_ZERO_OR_GREATER_ERROR,
  InvoiceStatusCategory,
  isFulfillmentShippingChargeValid,
  isPaidInvoiceConfirmBlocked,
  isPaidInvoiceStatus,
  PAID_INVOICE_POPUP_NOTE,
  shouldShowCurrentShippingHistory,
  UNPAID_INVOICE_POPUP_NOTE,
} from "./shippingMethodUtils";

export type ShippingMethodChangeConfirmData = {
  fulfillmentShippingMethod: string;
  fulfillmentShippingCharge: number;
  previousShippingCharge: number;
  invoiceStatusLabel: string;
  invoiceStatusCategory: InvoiceStatusCategory;
};

interface ShippingMethodChangePopupProps {
  open: boolean;
  requestedShippingMethod: string;
  requestedShippingCharge: number;
  currentShippingMethod?: string;
  currentShippingCharge?: number;
  fulfillmentShippingMethod: string;
  initialFulfillmentCharge: number;
  previousShippingCharge: number;
  invoiceStatusLabel: string;
  invoiceStatusCategory: InvoiceStatusCategory;
  invoiceStatus?: string | null;
  /** No longer gates a $0.00 charge — a zero New Shipping Charge is always accepted here. */
  allowFreeShipping?: boolean;
  onClose: () => void;
  onConfirm: (data: ShippingMethodChangeConfirmData) => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const invoiceStatusBadgeSx = (category: InvoiceStatusCategory, isDark: boolean) => {
  const styles: Record<InvoiceStatusCategory, { color: string; border: string; dot: string }> = {
    unpaid: {
      color: "#ff9f0a",
      border: "rgba(255,159,10,0.45)",
      dot: "#ff9f0a",
    },
    partial: {
      color: "#ff9f0a",
      border: "rgba(255,159,10,0.45)",
      dot: "#ff9f0a",
    },
    paid: {
      color: "#22c55e",
      border: "rgba(34,197,94,0.45)",
      dot: "#22c55e",
    },
    unknown: {
      color: "#f59e0b",
      border: "rgba(245,158,11,0.45)",
      dot: "#f59e0b",
    },
    not_created: {
      color: isDark ? "#9ba1a6" : "#64748b",
      border: isDark ? "rgba(155,161,166,0.35)" : "rgba(100,116,139,0.35)",
      dot: isDark ? "#9ba1a6" : "#64748b",
    },
  };
  return styles[category];
};

const ShippingMethodChangePopup: React.FC<ShippingMethodChangePopupProps> = ({
  open,
  requestedShippingMethod,
  requestedShippingCharge,
  currentShippingMethod,
  currentShippingCharge,
  fulfillmentShippingMethod,
  initialFulfillmentCharge,
  previousShippingCharge,
  invoiceStatusLabel,
  invoiceStatusCategory,
  invoiceStatus,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isDark,
    paperSx,
    tokens,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    cancelBtnSx,
    inputFieldWithLabelSx,
  } = useModalStyles();
  const isPaidInvoice =
    invoiceStatusCategory === "paid" || isPaidInvoiceStatus(invoiceStatus);
  const lockedCharge = previousShippingCharge;
  const [fulfillmentCharge, setFulfillmentCharge] = useState<number>(
    isPaidInvoice ? lockedCharge : initialFulfillmentCharge
  );

  useEffect(() => {
    if (!open) return;
    setFulfillmentCharge(isPaidInvoice ? lockedCharge : initialFulfillmentCharge);
  }, [open, isPaidInvoice, lockedCharge, initialFulfillmentCharge]);

  const effectiveCharge = isPaidInvoice ? lockedCharge : fulfillmentCharge;
  const selectedMethodCharge = extractShippingCharge(fulfillmentShippingMethod);
  const paidConfirmBlocked = isPaidInvoice
    && isPaidInvoiceConfirmBlocked(previousShippingCharge, fulfillmentShippingMethod, effectiveCharge);
  // New Shipping Charge accepts 0 — only negative / non-numeric values are rejected.
  const chargeInvalid = !isPaidInvoice
    && !isFulfillmentShippingChargeValid(effectiveCharge, { allowFreeShipping: true });
  const chargeErrorMessage = FULFILLMENT_SHIPPING_CHARGE_ZERO_OR_GREATER_ERROR;
  const confirmDisabled = paidConfirmBlocked || chargeInvalid;
  const noteColor = isPaidInvoice ? tokens.error : isDark ? "#F59E0B" : "#B45309";
  const statusBadge = invoiceStatusBadgeSx(invoiceStatusCategory, isDark);
  const showCurrentHistory = shouldShowCurrentShippingHistory(
    requestedShippingMethod,
    currentShippingMethod
  );

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm({
      fulfillmentShippingMethod,
      fulfillmentShippingCharge: effectiveCharge,
      previousShippingCharge,
      invoiceStatusLabel,
      invoiceStatusCategory,
    });
  };

  const sectionLabelSx = {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: tokens.textSecondary,
    mb: 0.75,
    letterSpacing: "0.01em",
  };

  const methodCardSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: 1.75,
    py: 1.5,
    borderRadius: "12px",
    border: `1px solid ${tokens.border}`,
    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.cardBg,
  };

  const renderWarningBox = (message: string, color = noteColor) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        mt: 1.5,
        px: 1.75,
        py: 1.5,
        borderRadius: "12px",
        border: `1px solid ${isDark ? "rgba(255,159,10,0.35)" : "rgba(245,158,11,0.45)"}`,
        backgroundColor: isDark ? "rgba(245,158,11,0.08)" : "rgba(255,251,235,0.9)",
      }}
    >
      <WarningAmberOutlined sx={{ color, fontSize: 20, mt: 0.1, flexShrink: 0 }} />
      <Typography sx={{ color, fontSize: "0.88rem", fontWeight: 500, lineHeight: 1.5 }}>
        {message}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: paperSx(isMobile) }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.72)" : "rgba(15,23,42,0.45)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      {/* Header */}
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <LocalShippingOutlined sx={{ color: tokens.accent, fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: 1, pr: 3 }}>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
          >
            Shipping Method Change
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            Update shipping information
          </Typography>
        </Box>

        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <Close sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ ...bodySx, gap: 2.25 }}>
        {/* Original Requested Method */}
        <Box>
          <Typography sx={sectionLabelSx}>Original Requested Method</Typography>
          <Box sx={methodCardSx}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
                flexShrink: 0,
              }}
            >
              <Inventory2Outlined sx={{ color: tokens.textPrimary, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                {formatShippingMethodLabel(requestedShippingMethod)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}>
                <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }}>
                  Shipping Charge
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: tokens.textPrimary }}>
                  {formatShippingCurrency(requestedShippingCharge)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {showCurrentHistory && (
          <Box>
            <Typography sx={sectionLabelSx}>Current Shipping Method</Typography>
            <Box sx={methodCardSx}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
                  flexShrink: 0,
                }}
              >
                <Inventory2Outlined sx={{ color: tokens.textPrimary, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                  {formatShippingMethodLabel(currentShippingMethod)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}>
                  <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }}>
                    Shipping Charge
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: tokens.textPrimary }}>
                    {formatShippingCurrency(currentShippingCharge)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Transition divider */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            my: 0.25,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: "1px",
              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.1)",
            }}
          />
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mx: 1.5,
              backgroundColor: tokens.bg,
              border: `1px solid ${tokens.accent}`,
            }}
          >
            <South sx={{ color: tokens.accent, fontSize: 16 }} />
          </Box>
          <Box
            sx={{
              flex: 1,
              height: "1px",
              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.1)",
            }}
          />
        </Box>

        {/* Updated Shipping Method */}
        <Box>
          <Typography sx={sectionLabelSx}>Updated Shipping Method</Typography>
          <Box
            sx={{
              ...methodCardSx,
              justifyContent: "space-between",
              cursor: "default",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.12)",
                  flexShrink: 0,
                }}
              >
                <Inventory2Outlined sx={{ color: tokens.accent, fontSize: 20 }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: tokens.textPrimary,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {formatShippingMethodLabel(fulfillmentShippingMethod)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* New Shipping Charge */}
        <Box>
          <Typography sx={sectionLabelSx}>
            New Shipping Charge{" "}
            <Box component="span" sx={{ color: tokens.accent }}>
              *
            </Box>
          </Typography>
          {isPaidInvoice ? (
            <Box
              sx={{
                ...methodCardSx,
                px: 2,
                py: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: tokens.textPrimary,
                  pl: 0.5,
                }}
              >
                {formatShippingCurrency(lockedCharge)}
              </Typography>
            </Box>
          ) : (
            <Box sx={inputFieldWithLabelSx(chargeInvalid)}>
              <NumericFormat
                customInput={TextField}
                value={fulfillmentCharge}
                fullWidth
                allowNegative={false}
                fixedDecimalScale
                thousandSeparator
                decimalScale={2}
                onValueChange={(vals) => setFulfillmentCharge(vals.floatValue ?? 0)}
                error={chargeInvalid}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: tokens.textSecondary, fontSize: "1.1rem", fontWeight: 500 }}>
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    "& input": { py: 1.5, px: 0.5 },
                  },
                }}
                helperText={chargeInvalid ? chargeErrorMessage : undefined}
                FormHelperTextProps={{
                  sx: { color: tokens.textSecondary, fontSize: "0.78rem", mt: 0.75, ml: 0.5 },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Invoice Status */}
        <Box>
          <Typography sx={sectionLabelSx}>Invoice Status</Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              border: `1px solid ${statusBadge.border}`,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: statusBadge.dot,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: statusBadge.color,
                lineHeight: 1,
              }}
            >
              {invoiceStatusLabel}
            </Typography>
          </Box>
        </Box>

        {/* Warning / info messages */}
        {isPaidInvoice && renderWarningBox(PAID_INVOICE_POPUP_NOTE.replace(/^\*/, ""))}

        {/* {paidConfirmBlocked && selectedMethodCharge != null && renderWarningBox(
          `Selected method charge (${formatShippingCurrency(selectedMethodCharge)}) must match the paid invoice charge (${formatShippingCurrency(previousShippingCharge)}).`
        )} */}

        {invoiceStatusCategory === "unknown" && renderWarningBox(
          "Unable to verify invoice status. Shipping cannot be changed until invoice status is confirmed."
        )}

        {invoiceStatusCategory === "unpaid" && renderWarningBox(
          UNPAID_INVOICE_POPUP_NOTE.replace(/^\*/, "")
        )}

        {invoiceStatusCategory === "not_created" && (
          <Typography sx={{ color: tokens.textSecondary, fontSize: "0.88rem", lineHeight: 1.5 }}>
            Review the fulfillment shipping details before continuing. You may override the charge if needed.
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ ...footerSx, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button
          onClick={handleConfirm}
          disabled={confirmDisabled}
          variant="contained"
          startIcon={<SaveOutlined sx={{ fontSize: "1rem !important" }} />}
          sx={primaryBtnSx}
        >
          Save Changes
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
          sx={{ ...cancelBtnSx, minWidth: 110 }}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
};

export default ShippingMethodChangePopup;
